import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, CircularProgress, Card, CardContent, Stack, Chip, Button, Alert,
  IconButton, Tooltip, Fade, Zoom, Avatar, LinearProgress, Collapse, Badge, alpha,
  useTheme as useMuiTheme
} from '@mui/material';
import {
  ArrowBack, Security as SecurityIcon, Save as SaveIcon, Refresh as RefreshIcon,
  CheckCircle as CheckIcon, Cancel as CancelIcon, RemoveCircle as InheritIcon,
  Person as PersonIcon, Shield as ShieldIcon, Lock as LockIcon, ExpandMore, ExpandLess,
  Inventory as InventoryIcon, ShoppingCart as PurchaseIcon, AccountBalance as AccountingIcon,
  Assessment as ReportsIcon, Settings as SettingsIcon, People as UsersIcon,
  Category as CategoryIcon, Warehouse as WarehouseIcon, Store as BranchIcon,
  Done as DoneIcon, Close as CloseIcon, HorizontalRule as HorizontalRuleIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { toast } from 'react-toastify';
import { useTheme } from '../../contexts/ThemeContext';
import axiosInstance from '../../services/axiosInstance';

const translations = {
  en: {
    title: 'User Permissions', subtitle: 'Customize access permissions for this user',
    back: 'Back', save: 'Save Changes', refresh: 'Refresh',
    permission: 'Permission', module: 'Module', roleDefault: 'Role Default', customAccess: 'Custom Access',
    granted: 'Granted', denied: 'Denied', inherit: 'Inherit from Role', loading: 'Loading permissions...', 
    saved: 'Permissions saved successfully!', saving: 'Saving...',
    noPermissions: 'No permissions found', user: 'User', totalPermissions: 'Total Permissions',
    customized: 'Customized', inherited: 'Inherited', grantedCount: 'Granted',
    fromRole: 'From Role', expandAll: 'Expand All', collapseAll: 'Collapse All',
    permissionsInModule: 'permissions', searchPlaceholder: 'Search permissions...'
  },
  ar: {
    title: 'صلاحيات المستخدم', subtitle: 'تخصيص صلاحيات الوصول لهذا المستخدم',
    back: 'رجوع', save: 'حفظ التغييرات', refresh: 'تحديث',
    permission: 'الصلاحية', module: 'الوحدة', roleDefault: 'افتراضي الدور', customAccess: 'وصول مخصص',
    granted: 'ممنوح', denied: 'ممنوع', inherit: 'وراثة من الدور', loading: 'جاري تحميل الصلاحيات...', 
    saved: 'تم حفظ الصلاحيات بنجاح!', saving: 'جاري الحفظ...',
    noPermissions: 'لا توجد صلاحيات', user: 'المستخدم', totalPermissions: 'إجمالي الصلاحيات',
    customized: 'مخصصة', inherited: 'موروثة', grantedCount: 'ممنوحة',
    fromRole: 'من الدور', expandAll: 'توسيع الكل', collapseAll: 'طي الكل',
    permissionsInModule: 'صلاحية', searchPlaceholder: 'بحث في الصلاحيات...'
  },
};

interface Permission { permissionId: number; permissionName: string; permissionCode: string; moduleName: string; }
interface UserPermission { permissionId: number; isGranted: boolean; }
interface User { userId: number; userName: string; fullName: string; fullNameAr?: string; }

const UserPermissionsPage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { mode } = useTheme();
  const muiTheme = useMuiTheme();
  const t = translations[language];
  const isRTL = language === 'ar';
  const isDark = mode === 'dark';

  const [user, setUser] = useState<User | null>(null);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [rolePermissions, setRolePermissions] = useState<number[]>([]);
  const [userPermissions, setUserPermissions] = useState<Map<number, boolean | null>>(new Map());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedModules, setExpandedModules] = useState<Set<string>>(new Set());

  // Theme-aware colors
  const colors = {
    primary: isDark ? '#90caf9' : '#667eea',
    secondary: isDark ? '#ce93d8' : '#764ba2',
    success: isDark ? '#66bb6a' : '#4caf50',
    error: isDark ? '#f44336' : '#f44336',
    warning: isDark ? '#ffb74d' : '#ff9800',
    headerBg: isDark 
      ? 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)' 
      : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    cardBg: isDark ? '#1e1e1e' : '#ffffff',
    hoverBg: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(102, 126, 234, 0.04)',
    border: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)',
  };

  useEffect(() => { loadData(); }, [userId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [userRes, permsRes, userPermsRes] = await Promise.all([
        axiosInstance.get(`/Users/${userId}`),
        axiosInstance.get('/Permissions'),
        axiosInstance.get(`/Users/${userId}/permissions`)
      ]);
      setUser(userRes.data);
      setPermissions(permsRes.data);
      setRolePermissions(userRes.data.rolePermissionIds || []);
      
      const upMap = new Map<number, boolean | null>();
      userPermsRes.data.forEach((up: UserPermission) => upMap.set(up.permissionId, up.isGranted));
      setUserPermissions(upMap);

      // Expand first module by default
      const modules = Array.from(new Set(permsRes.data.map((p: Permission) => p.moduleName))) as string[];
      if (modules.length > 0) setExpandedModules(new Set([modules[0]]));
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handlePermissionChange = (permId: number, value: boolean | null) => {
    const newMap = new Map(userPermissions);
    if (value === null) newMap.delete(permId);
    else newMap.set(permId, value);
    setUserPermissions(newMap);
  };

  const getPermissionStatus = (permId: number): 'granted' | 'denied' | 'inherit' => {
    if (userPermissions.has(permId)) return userPermissions.get(permId) ? 'granted' : 'denied';
    return 'inherit';
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const data = Array.from(userPermissions.entries()).map(([permissionId, isGranted]) => ({ permissionId, isGranted }));
      await axiosInstance.put(`/Users/${userId}/permissions`, data);
      toast.success(t.saved);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || (isRTL ? 'فشل الحفظ' : 'Failed to save'));
    }
    finally { setSaving(false); }
  };

  const toggleModule = (moduleName: string) => {
    const newExpanded = new Set(expandedModules);
    if (newExpanded.has(moduleName)) newExpanded.delete(moduleName);
    else newExpanded.add(moduleName);
    setExpandedModules(newExpanded);
  };

  const expandAll = () => {
    setExpandedModules(new Set(Object.keys(groupedPermissions)));
  };

  const collapseAll = () => {
    setExpandedModules(new Set());
  };

  const groupedPermissions = permissions.reduce((acc, p) => {
    if (!acc[p.moduleName]) acc[p.moduleName] = [];
    acc[p.moduleName].push(p);
    return acc;
  }, {} as Record<string, Permission[]>);

  const stats = {
    total: permissions.length,
    customized: userPermissions.size,
    inherited: permissions.length - userPermissions.size,
    granted: Array.from(userPermissions.values()).filter(v => v === true).length + 
             rolePermissions.filter(id => !userPermissions.has(id) || userPermissions.get(id) !== false).length
  };

  const getModuleIcon = (module: string) => {
    const icons: Record<string, React.ReactNode> = {
      'Inventory': <InventoryIcon />,
      'المخزون': <InventoryIcon />,
      'Purchases': <PurchaseIcon />,
      'المشتريات': <PurchaseIcon />,
      'Accounting': <AccountingIcon />,
      'المحاسبة': <AccountingIcon />,
      'Reports': <ReportsIcon />,
      'التقارير': <ReportsIcon />,
      'Settings': <SettingsIcon />,
      'الإعدادات': <SettingsIcon />,
      'Users': <UsersIcon />,
      'المستخدمين': <UsersIcon />,
      'Categories': <CategoryIcon />,
      'التصنيفات': <CategoryIcon />,
      'Warehouses': <WarehouseIcon />,
      'المخازن': <WarehouseIcon />,
      'Branches': <BranchIcon />,
      'الفروع': <BranchIcon />,
    };
    return icons[module] || <ShieldIcon />;
  };

  const getModuleGradient = (index: number) => {
    const gradients = isDark ? [
      'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
      'linear-gradient(135deg, #8e2de2 0%, #4a00e0 100%)',
      'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
      'linear-gradient(135deg, #fc4a1a 0%, #f7b733 100%)',
      'linear-gradient(135deg, #ee0979 0%, #ff6a00 100%)',
      'linear-gradient(135deg, #00b4db 0%, #0083b0 100%)',
    ] : [
      'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
      'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
      'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
    ];
    return gradients[index % gradients.length];
  };

  const getModuleStats = (perms: Permission[]) => {
    let granted = 0, denied = 0, inherited = 0;
    perms.forEach(p => {
      const status = getPermissionStatus(p.permissionId);
      if (status === 'granted') granted++;
      else if (status === 'denied') denied++;
      else inherited++;
    });
    return { granted, denied, inherited, total: perms.length };
  };

  if (loading) return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      justifyContent: 'center', 
      alignItems: 'center', 
      minHeight: '70vh', 
      gap: 3 
    }}>
      <Box sx={{ position: 'relative' }}>
        <CircularProgress size={80} thickness={2} sx={{ color: alpha(colors.primary, 0.2) }} />
        <CircularProgress 
          size={80} 
          thickness={2} 
          sx={{ 
            color: colors.primary, 
            position: 'absolute', 
            left: 0,
            animationDuration: '1.5s'
          }} 
        />
        <Box sx={{ 
          position: 'absolute', 
          top: '50%', 
          left: '50%', 
          transform: 'translate(-50%, -50%)' 
        }}>
          <SecurityIcon sx={{ fontSize: 32, color: colors.primary }} />
        </Box>
      </Box>
      <Typography variant="h6" color="text.secondary" fontWeight="medium">{t.loading}</Typography>
    </Box>
  );

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: 'auto' }} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <Fade in timeout={500}>
        <Paper 
          elevation={0} 
          sx={{ 
            p: 3, 
            mb: 4, 
            borderRadius: 4, 
            background: colors.headerBg, 
            color: 'white',
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          {/* Background Pattern */}
          <Box sx={{ 
            position: 'absolute', 
            top: -50, 
            right: -50, 
            width: 200, 
            height: 200, 
            borderRadius: '50%', 
            bgcolor: 'rgba(255,255,255,0.1)' 
          }} />
          <Box sx={{ 
            position: 'absolute', 
            bottom: -30, 
            left: '30%', 
            width: 100, 
            height: 100, 
            borderRadius: '50%', 
            bgcolor: 'rgba(255,255,255,0.05)' 
          }} />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, position: 'relative', zIndex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Tooltip title={t.back} arrow>
                <IconButton 
                  onClick={() => navigate(-1)} 
                  sx={{ 
                    color: 'white', 
                    bgcolor: 'rgba(255,255,255,0.15)', 
                    backdropFilter: 'blur(10px)',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.25)', transform: 'scale(1.05)' },
                    transition: 'all 0.2s ease'
                  }}
                >
                  <ArrowBack />
                </IconButton>
              </Tooltip>
              <Badge
                overlap="circular"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                badgeContent={
                  <Box sx={{ 
                    width: 16, 
                    height: 16, 
                    borderRadius: '50%', 
                    bgcolor: colors.success, 
                    border: '2px solid white',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                  }} />
                }
              >
                <Avatar sx={{ 
                  bgcolor: 'rgba(255,255,255,0.2)', 
                  width: 64, 
                  height: 64,
                  backdropFilter: 'blur(10px)',
                  border: '3px solid rgba(255,255,255,0.3)'
                }}>
                  <PersonIcon sx={{ fontSize: 36 }} />
                </Avatar>
              </Badge>
              <Box>
                <Typography variant="h5" fontWeight="bold" sx={{ mb: 0.5 }}>{t.title}</Typography>
                <Box sx={{ opacity: 0.95, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SecurityIcon sx={{ fontSize: 18 }} />
                  {user?.fullName || user?.fullNameAr} 
                  <Chip 
                    label={`@${user?.userName}`} 
                    size="small" 
                    sx={{ 
                      bgcolor: 'rgba(255,255,255,0.2)', 
                      color: 'white', 
                      fontWeight: 500,
                      height: 24
                    }} 
                  />
                </Box>
              </Box>
            </Box>
            <Stack direction="row" spacing={1.5}>
              <Tooltip title={t.refresh} arrow>
                <IconButton 
                  onClick={loadData} 
                  sx={{ 
                    color: 'white', 
                    bgcolor: 'rgba(255,255,255,0.15)', 
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.25)', transform: 'rotate(180deg)' },
                    transition: 'all 0.3s ease'
                  }}
                >
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              <Button 
                variant="contained" 
                startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />} 
                onClick={handleSave} 
                disabled={saving}
                sx={{ 
                  bgcolor: 'rgba(255,255,255,0.2)', 
                  color: 'white', 
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.3)', transform: 'translateY(-2px)' }, 
                  borderRadius: 2, 
                  px: 3,
                  backdropFilter: 'blur(10px)',
                  boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
                  transition: 'all 0.2s ease'
                }}
              >
                {saving ? t.saving : t.save}
              </Button>
            </Stack>
          </Box>
          
          {/* Progress bar while saving */}
          {saving && (
            <LinearProgress 
              sx={{ 
                position: 'absolute', 
                bottom: 0, 
                left: 0, 
                right: 0, 
                height: 3,
                bgcolor: 'rgba(255,255,255,0.2)',
                '& .MuiLinearProgress-bar': { bgcolor: 'white' }
              }} 
            />
          )}
          
        </Paper>
      </Fade>

      {/* Stats Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2, mb: 4 }}>
        {[
          { value: stats.total, label: t.totalPermissions, icon: <SecurityIcon />, gradient: colors.headerBg, delay: 0 },
          { value: stats.granted, label: t.grantedCount, icon: <CheckIcon />, gradient: isDark ? 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' : 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)', delay: 100 },
          { value: stats.customized, label: t.customized, icon: <LockIcon />, gradient: isDark ? 'linear-gradient(135deg, #8e2de2 0%, #4a00e0 100%)' : 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', delay: 200 },
          { value: stats.inherited, label: t.inherited, icon: <InheritIcon />, gradient: isDark ? 'linear-gradient(135deg, #00b4db 0%, #0083b0 100%)' : 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', delay: 300 },
        ].map((stat, index) => (
          <Zoom in timeout={400 + stat.delay} key={stat.label}>
            <Card 
              elevation={0} 
              sx={{ 
                background: stat.gradient, 
                color: 'white', 
                borderRadius: 3, 
                transition: 'all 0.3s ease', 
                cursor: 'default',
                position: 'relative',
                overflow: 'hidden',
                '&:hover': { 
                  transform: 'translateY(-8px)', 
                  boxShadow: '0 20px 40px rgba(0,0,0,0.25)' 
                } 
              }}
            >
              <Box sx={{ 
                position: 'absolute', 
                top: -20, 
                right: -20, 
                width: 80, 
                height: 80, 
                borderRadius: '50%', 
                bgcolor: 'rgba(255,255,255,0.1)' 
              }} />
              <CardContent sx={{ p: 2.5, textAlign: 'center', position: 'relative', zIndex: 1 }}>
                <Box sx={{ 
                  display: 'inline-flex', 
                  p: 1.5, 
                  borderRadius: 2, 
                  bgcolor: 'rgba(255,255,255,0.2)',
                  mb: 1.5
                }}>
                  {stat.icon}
                </Box>
                <Typography variant="h3" fontWeight="bold" sx={{ mb: 0.5 }}>{stat.value}</Typography>
                <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 500 }}>{stat.label}</Typography>
              </CardContent>
            </Card>
          </Zoom>
        ))}
      </Box>

      {/* Expand/Collapse All Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 2 }}>
        <Button 
          size="small" 
          variant="outlined" 
          onClick={expandAll}
          startIcon={<ExpandMore />}
          sx={{ 
            borderColor: colors.border, 
            color: colors.primary,
            borderRadius: 2,
            '&:hover': { borderColor: colors.primary, bgcolor: alpha(colors.primary, 0.05) }
          }}
        >
          {t.expandAll}
        </Button>
        <Button 
          size="small" 
          variant="outlined" 
          onClick={collapseAll}
          startIcon={<ExpandLess />}
          sx={{ 
            borderColor: colors.border, 
            color: colors.primary,
            borderRadius: 2,
            '&:hover': { borderColor: colors.primary, bgcolor: alpha(colors.primary, 0.05) }
          }}
        >
          {t.collapseAll}
        </Button>
      </Box>

      {/* Permission Modules */}
      {Object.entries(groupedPermissions).map(([module, perms], moduleIndex) => {
        const moduleStats = getModuleStats(perms);
        const isExpanded = expandedModules.has(module);
        
        return (
          <Fade in timeout={500 + moduleIndex * 100} key={module}>
            <Card 
              elevation={0} 
              sx={{ 
                mb: 2, 
                borderRadius: 3, 
                overflow: 'hidden', 
                border: `1px solid ${colors.border}`,
                bgcolor: colors.cardBg,
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: isDark 
                    ? '0 8px 32px rgba(0,0,0,0.3)' 
                    : '0 8px 32px rgba(102, 126, 234, 0.15)'
                }
              }}
            >
              {/* Module Header */}
              <Box 
                onClick={() => toggleModule(module)}
                sx={{ 
                  background: getModuleGradient(moduleIndex), 
                  p: 2, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': { filter: 'brightness(1.1)' }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ 
                    p: 1.5, 
                    bgcolor: 'rgba(255,255,255,0.2)', 
                    borderRadius: 2,
                    display: 'flex',
                    backdropFilter: 'blur(10px)'
                  }}>
                    {getModuleIcon(module)}
                  </Box>
                  <Box>
                    <Typography variant="h6" fontWeight="bold" sx={{ color: 'white' }}>{module}</Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                      {perms.length} {t.permissionsInModule}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  {/* Mini Stats */}
                  <Stack direction="row" spacing={1}>
                    <Chip 
                      icon={<DoneIcon sx={{ fontSize: 16, color: 'white !important' }} />}
                      label={moduleStats.granted} 
                      size="small" 
                      sx={{ bgcolor: 'rgba(76, 175, 80, 0.8)', color: 'white', fontWeight: 'bold' }} 
                    />
                    <Chip 
                      icon={<CloseIcon sx={{ fontSize: 16, color: 'white !important' }} />}
                      label={moduleStats.denied} 
                      size="small" 
                      sx={{ bgcolor: 'rgba(244, 67, 54, 0.8)', color: 'white', fontWeight: 'bold' }} 
                    />
                    <Chip 
                      icon={<HorizontalRuleIcon sx={{ fontSize: 16, color: 'white !important' }} />}
                      label={moduleStats.inherited} 
                      size="small" 
                      sx={{ bgcolor: 'rgba(158, 158, 158, 0.8)', color: 'white', fontWeight: 'bold' }} 
                    />
                  </Stack>
                  <IconButton 
                    size="small" 
                    sx={{ 
                      color: 'white', 
                      bgcolor: 'rgba(255,255,255,0.2)',
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }
                    }}
                  >
                    {isExpanded ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                </Box>
              </Box>

              {/* Permissions List */}
              <Collapse in={isExpanded}>
                <CardContent sx={{ p: 0 }}>
                  {perms.map((perm, permIndex) => {
                    const status = getPermissionStatus(perm.permissionId);
                    const hasRoleAccess = rolePermissions.includes(perm.permissionId);
                    
                    return (
                      <Box 
                        key={perm.permissionId} 
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'space-between', 
                          p: 2,
                          px: 3,
                          borderBottom: permIndex < perms.length - 1 ? `1px solid ${colors.border}` : 'none', 
                          '&:hover': { bgcolor: colors.hoverBg }, 
                          transition: 'all 0.2s ease' 
                        }}
                      >
                        <Box sx={{ flex: 1 }}>
                          <Typography fontWeight="600" sx={{ mb: 0.5, color: isDark ? '#fff' : '#1a1a1a' }}>
                            {perm.permissionName}
                          </Typography>
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              fontFamily: 'monospace', 
                              bgcolor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                              px: 1,
                              py: 0.25,
                              borderRadius: 1,
                              color: isDark ? '#90caf9' : '#667eea'
                            }}
                          >
                            {perm.permissionCode}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          {/* Role Default Status */}
                          <Tooltip title={t.fromRole} arrow>
                            <Chip 
                              icon={hasRoleAccess ? <CheckIcon sx={{ fontSize: 16 }} /> : <CancelIcon sx={{ fontSize: 16 }} />}
                              label={hasRoleAccess ? t.granted : t.denied} 
                              size="small" 
                              sx={{ 
                                bgcolor: hasRoleAccess 
                                  ? alpha(colors.success, isDark ? 0.2 : 0.1)
                                  : alpha(colors.error, isDark ? 0.2 : 0.1),
                                color: hasRoleAccess ? colors.success : colors.error,
                                fontWeight: 600,
                                minWidth: 90,
                                border: `1px solid ${hasRoleAccess ? alpha(colors.success, 0.3) : alpha(colors.error, 0.3)}`
                              }} 
                            />
                          </Tooltip>
                          
                          {/* Permission Action Buttons */}
                          <Stack direction="row" spacing={0.5}>
                            {[
                              { value: null, label: t.inherit, icon: <InheritIcon />, color: '#9e9e9e', activeColor: isDark ? '#b0b0b0' : '#757575' },
                              { value: true, label: t.granted, icon: <CheckIcon />, color: colors.success, activeColor: colors.success },
                              { value: false, label: t.denied, icon: <CancelIcon />, color: colors.error, activeColor: colors.error },
                            ].map((opt) => {
                              const isActive = status === (opt.value === null ? 'inherit' : opt.value ? 'granted' : 'denied');
                              return (
                                <Tooltip key={String(opt.value)} title={opt.label} arrow placement="top">
                                  <IconButton 
                                    size="small" 
                                    onClick={() => handlePermissionChange(perm.permissionId, opt.value)}
                                    sx={{
                                      color: isActive ? opt.activeColor : alpha(isDark ? '#fff' : '#000', 0.3),
                                      bgcolor: isActive ? alpha(opt.color, isDark ? 0.25 : 0.15) : 'transparent',
                                      border: `2px solid ${isActive ? opt.color : 'transparent'}`,
                                      '&:hover': { 
                                        bgcolor: alpha(opt.color, 0.2),
                                        transform: 'scale(1.1)'
                                      },
                                      transition: 'all 0.2s ease',
                                      width: 36,
                                      height: 36
                                    }}
                                  >
                                    {opt.icon}
                                  </IconButton>
                                </Tooltip>
                              );
                            })}
                          </Stack>
                        </Box>
                      </Box>
                    );
                  })}
                </CardContent>
              </Collapse>
            </Card>
          </Fade>
        );
      })}
    </Box>
  );
};

export default UserPermissionsPage;